import { TYPE_CONTENT_PACKAGE } from "../../constants/page";

/**
 * "Reflows" the cards in page sections, to accomodate native ad injection
 * Finds all sections that have the same pubQueueId, and divides cards appropriately based on the section wrapper's size
 * @param {*} sections current page sections
 **/
export const reflowPageSections = (sections) => {
    if (!sections) {
        return sections;
    }

    const pubQMap = new Map();

    // Check if each page section has as many cards as its wrapper size.
    // If not, concat all cards with the same pubQueueId into one array, and parcel them out.
    // This allows us to inject ads while still maintaining card order.
    sections.forEach((section, sectionIndex) => {
        section?.main?.content?.forEach(
            (content, contentIndex, contentArray) => {
                const pubQId = content?.lineup?.pubQueueId;

                if (pubQId && content?.content && content?.wrapper) {
                    // contentPackages, when first in a section, are parsed into their own arrays
                    // thus they need special handling here
                    const includesContentPackage =
                        contentArray[0] &&
                        contentArray[0].type === TYPE_CONTENT_PACKAGE;
                    const contentPackageSize = includesContentPackage ? 1 : 0;

                    const needsReflow =
                        content.content.length + contentPackageSize !==
                        content?.wrapper?.size;

                    const oldData = pubQMap.get(pubQId) || {
                        needsReflow,
                        flattenedContent: [],
                        contentPackageSectionIndex: null,
                    };

                    // build a map with all the data we need to reflow the cards across sections
                    pubQMap.set(pubQId, {
                        needsReflow: needsReflow || oldData.needsReflow,
                        flattenedContent: [
                            ...oldData.flattenedContent,
                            ...content.content,
                        ],
                        contentPackageSectionIndex: includesContentPackage
                            ? sectionIndex
                            : oldData.contentPackageSectionIndex,
                        lastSectionIndex: sectionIndex,
                    });
                }
            }
        );
    });

    // avoid mutating the original `sections`, to prevent Redux errors/warnings
    const output = sections.map((section, sectionIndex) => {
        return {
            ...section,
            main: {
                ...section.main,
                content: section.main.content.map((sectionContent) => {
                    if (
                        !sectionContent.lineup?.pubQueueId ||
                        !sectionContent.wrapper
                    ) {
                        return sectionContent;
                    }

                    const mappedData = pubQMap.get(
                        sectionContent.lineup.pubQueueId
                    );
                    if (!mappedData.needsReflow) {
                        return sectionContent;
                    }

                    let start = sectionContent.wrapper.startAt - 1;
                    let end = start + sectionContent.wrapper.size;

                    const pubQIdIncludesContentPackage =
                        mappedData.contentPackageSectionIndex !== null;
                    if (pubQIdIncludesContentPackage) {
                        start = Math.max(0, start - 1);
                    }

                    // contentpackages are parsed into their own element in the 'section.main.content' array.
                    // This means they aren't counted in the 'sectionContent.wrapper.size' value.
                    const sectionIncludesContentPackage =
                        mappedData.contentPackageSectionIndex === sectionIndex;
                    if (sectionIncludesContentPackage) {
                        end -= 1;
                    }

                    // for the last section, allow all cards to remain
                    // If an ad is injected, then removed, this prevents the last section
                    // from having its last card(s) sliced off, leaving it short
                    if (sectionIndex === mappedData.lastSectionIndex) {
                        end = undefined;
                    }
                    return {
                        ...sectionContent,
                        content: mappedData.flattenedContent.slice(start, end),
                    };
                }),
            },
        };
    });
    return output;
};
